import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import PageHeader from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  TrendingUp, CheckCircle2,
  Euro, Target, Award, Users
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { format, subDays } from 'date-fns';
import { getOrderTotal } from '@/lib/orderFields';
import { de } from 'date-fns/locale';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

const AVATAR_GRADIENTS = [
  'from-blue-500 to-blue-700',
  'from-emerald-500 to-emerald-700',
  'from-violet-500 to-violet-700',
  'from-orange-500 to-orange-700',
  'from-rose-500 to-rose-700',
  'from-teal-500 to-teal-700',
  'from-indigo-500 to-indigo-700',
  'from-amber-500 to-amber-700',
];

function getAvatarGradient(str = '') {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
}

function getDisplayName(u) {
  if (u.vorname || u.nachname) return [u.vorname, u.nachname].filter(Boolean).join(' ');
  if (u.name) return u.name;
  if (u.full_name) return u.full_name;
  return u.email?.split('@')[0] || 'Agent';
}

function getInitials(name = '') {
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';
}

function getRole(u) {
  return (u.rolle || u.role || 'agent').toLowerCase();
}

export default function AdminAgentPerformance() {
  const [selectedAgent, setSelectedAgent] = useState(null);

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => base44.entities.User.list(),
  });

  const { data: leads = [] } = useQuery({
    queryKey: ['leads'],
    queryFn: () => base44.entities.Lead.list(),
  });

  const { data: tasks = [] } = useQuery({
    queryKey: ['tasks'],
    queryFn: () => base44.entities.Task.list(),
  });

  const { data: orders = [] } = useQuery({
    queryKey: ['orders'],
    queryFn: () => base44.entities.Order.list(),
  });

  const { data: activities = [] } = useQuery({
    queryKey: ['activities'],
    queryFn: () => base44.entities.ActivityReport.list(),
  });

  const agents = users.filter(u => {
    const r = getRole(u);
    return r === 'agent' || r === 'admin' || r === 'user';
  });

  const agentStats = agents.map(agent => {
    const agentLeads = leads.filter(l => l.assigned_agent === agent.email);
    const agentTasks = tasks.filter(t => t.assigned_agent === agent.email);
    const agentOrders = orders.filter(o => o.agent_email === agent.email);
    const agentActivities = activities.filter(a => a.agent_email === agent.email);

    const completedTasks = agentTasks.filter(t => t.status === 'Abgeschlossen').length;
    const openTasks = agentTasks.filter(t => t.status === 'Offen').length;
    // Ciro: iptaller ve teklifler hariç; tutar total → gesamtsumme → subtotal
    const totalRevenue = agentOrders
      .filter(o => o.status !== 'Storniert' && o.order_type !== 'Angebot')
      .reduce((sum, o) => sum + (getOrderTotal(o) || o.subtotal || 0), 0);
    const customers = agentLeads.filter(l => l.status === 'Kunde').length;

    const last7DaysActivity = Array.from({ length: 7 }, (_, i) => {
      const date = subDays(new Date(), 6 - i);
      const dateStr = format(date, 'yyyy-MM-dd');
      return {
        date: format(date, 'EE', { locale: de }),
        count: agentActivities.filter(a => a.report_date === dateStr).length
      };
    });

    const taskTypes = {};
    agentTasks.forEach(t => { taskTypes[t.task_type] = (taskTypes[t.task_type] || 0) + 1; });
    const taskTypeData = Object.entries(taskTypes).map(([name, value]) => ({ name, value }));

    return {
      agent,
      stats: {
        totalLeads: agentLeads.length,
        customers,
        completedTasks,
        openTasks,
        totalOrders: agentOrders.length,
        totalRevenue,
        totalActivities: agentActivities.length,
        conversionRate: agentLeads.length > 0 ? ((customers / agentLeads.length) * 100).toFixed(1) : 0,
      },
      charts: { last7DaysActivity, taskTypeData }
    };
  });

  const selected = selectedAgent
    ? agentStats.find(s => s.agent.email === selectedAgent)
    : null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <PageHeader
              title="Agent Performance"
              subtitle={<>Leistungsübersicht aller Agenten</>}
            />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-24">

        {/* Agent Selection Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
          {agentStats.length === 0 && (
            <div className="col-span-full flex flex-col items-center py-16 text-slate-400">
              <Users className="w-12 h-12 mb-3 text-slate-300" />
              <p>Keine Agenten gefunden</p>
            </div>
          )}
          {agentStats.map(({ agent, stats }) => {
            const displayName = getDisplayName(agent);
            const initials = getInitials(displayName);
            const gradient = getAvatarGradient(agent.email);
            const isAdmin = getRole(agent) === 'admin';
            const isSelected = selectedAgent === agent.email;

            return (
              <Card
                key={agent.email}
                className={`cursor-pointer transition-all duration-200 overflow-hidden ${isSelected
                  ? 'ring-2 ring-blue-500 shadow-lg shadow-blue-100'
                  : 'hover:shadow-md hover:-translate-y-0.5'
                  }`}
                onClick={() => setSelectedAgent(agent.email)}
              >
                <CardContent className="p-0">
                  {/* Gradient Avatar Banner */}
                  <div className={`bg-gradient-to-br ${gradient} px-4 pt-5 pb-4 flex flex-col items-center`}>
                    <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center mb-2 border-2 border-white/30">
                      <span className="text-2xl font-bold text-white tracking-wide">{initials}</span>
                    </div>
                    {/* Role badge */}
                    <span className="text-xs px-2.5 py-0.5 rounded-full font-semibold bg-white/25 text-white">
                      {isAdmin ? '👑 Admin' : '🧑‍💼 Agent'}
                    </span>
                  </div>

                  {/* Name, email & stats */}
                  <div className="p-3">
                    <p className="font-semibold text-slate-900 text-sm text-center truncate mb-0.5">
                      {displayName}
                    </p>
                    <p className="text-xs text-slate-400 text-center truncate mb-3">
                      {agent.email}
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-emerald-50 rounded-lg p-2 text-center">
                        <p className="text-xs text-emerald-600 font-medium">Kunden</p>
                        <p className="font-bold text-emerald-700 text-lg leading-tight">{stats.customers}</p>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-2 text-center">
                        <p className="text-xs text-blue-600 font-medium">Umsatz</p>
                        <p className="font-bold text-blue-700 text-lg leading-tight">
                          {stats.totalRevenue >= 1000
                            ? `€${(stats.totalRevenue / 1000).toFixed(1)}k`
                            : `€${stats.totalRevenue.toFixed(0)}`}
                        </p>
                      </div>
                    </div>
                    {/* Open tasks pulse indicator */}
                    {stats.openTasks > 0 && (
                      <div className="mt-2 flex items-center justify-center gap-1.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                        <p className="text-xs text-amber-600">{stats.openTasks} offene Aufgaben</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Detailed View */}
        {selected ? (
          <div className="space-y-6">
            {/* Selected agent info bar */}
            <div className="flex items-center gap-3 p-4 bg-white rounded-xl border shadow-sm">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getAvatarGradient(selected.agent.email)} flex items-center justify-center flex-shrink-0`}>
                <span className="text-base font-bold text-white">{getInitials(getDisplayName(selected.agent))}</span>
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-bold text-slate-900 truncate">{getDisplayName(selected.agent)}</h2>
                <p className="text-sm text-slate-500 truncate">{selected.agent.email}</p>
              </div>
              <Badge variant="outline" className="flex-shrink-0">
                {getRole(selected.agent) === 'admin' ? '👑 Admin' : '🧑‍💼 Agent'}
              </Badge>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-brand-950 text-white border-0">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm">Kunden</p>
                      <p className="text-3xl font-bold">{selected.stats.customers}</p>
                      <p className="text-xs text-blue-100 mt-1">{selected.stats.conversionRate}% Conversion</p>
                    </div>
                    <Target className="w-10 h-10 text-blue-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-emerald-600 text-white border-0">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-emerald-100 text-sm">Umsatz</p>
                      <p className="text-3xl font-bold">
                        €{selected.stats.totalRevenue.toLocaleString('de-DE', { maximumFractionDigits: 0 })}
                      </p>
                      <p className="text-xs text-emerald-100 mt-1">{selected.stats.totalOrders} Aufträge</p>
                    </div>
                    <Euro className="w-10 h-10 text-emerald-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-accent text-accent-foreground border-0">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-amber-100 text-sm">Aufgaben</p>
                      <p className="text-3xl font-bold">{selected.stats.completedTasks}</p>
                      <p className="text-xs text-amber-100 mt-1">{selected.stats.openTasks} offen</p>
                    </div>
                    <CheckCircle2 className="w-10 h-10 text-amber-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-700 text-white border-0">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100 text-sm">Aktivitäten</p>
                      <p className="text-3xl font-bold">{selected.stats.totalActivities}</p>
                      <p className="text-xs text-purple-100 mt-1">Letzte 30 Tage</p>
                    </div>
                    <TrendingUp className="w-10 h-10 text-purple-200" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts */}
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Aktivität (letzte 7 Tage)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={selected.charts.last7DaysActivity}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                        <XAxis dataKey="date" stroke="#94a3b8" />
                        <YAxis stroke="#94a3b8" allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Aufgabentypen</CardTitle>
                </CardHeader>
                <CardContent>
                  {selected.charts.taskTypeData.length > 0 ? (
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={selected.charts.taskTypeData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                            outerRadius={80}
                            dataKey="value"
                          >
                            {selected.charts.taskTypeData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-slate-400">
                      Keine Aufgaben vorhanden
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <Award className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p className="text-lg font-semibold text-slate-600 mb-2">
                Wählen Sie einen Agent aus
              </p>
              <p className="text-sm text-slate-500">
                Klicken Sie auf einen Agent um die Performance Details zu sehen
              </p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
